import React, { useMemo } from 'react';

import { Card, Divider, Flex } from 'antd-v5';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Heading, Text } from 'scouting-ui/lib';

import type { CounselorYouth } from '@appTypes/esb';
import { BaseMessage } from '@modules/meritBadgeCounselors/mbcBulkAssign/components/Messages';
import { ConditionalRender } from '@shared/components';

import { selectedYouthByMIDSel, validationErrorsSel } from '../duck/selectors';
import { bulkEntryActions } from '../duck/slice';
import styles from './BulkMBCEntryPage.module.scss';
import { YouthItem } from './YouthItem';

interface YouthSelectionProps {
  youths: CounselorYouth[];
}

export default function YouthSelection(props: YouthSelectionProps) {
  const { youths } = props;
  const dispatch = useDispatch();
  const errors = useSelector(validationErrorsSel);
  const selectedYouthByMID = useSelector(selectedYouthByMIDSel);

  const { eligible, notEligible } = useMemo(() => {
    const grouped = _.chain(youths || [])
      .orderBy(['lastName'], ['asc'])
      .groupBy(youth => (youth.memberId in errors ? 'invalid' : 'valid'))
      .value();
    return {
      eligible: grouped.valid || [],
      notEligible: grouped.invalid || [],
    };
  }, [errors, youths]);

  return (
    <Card>
      <Flex vertical gap="small">
        <Heading font="display-h5">
          <FormattedMessage id="page.mbcBulkEntry.selectYouth" />
          <Divider className={styles.selectYouthDivider} />
        </Heading>
        {eligible.map(youth => (
          <YouthItem
            checked={selectedYouthByMID.has(youth.memberId)}
            onSelect={checked =>
              dispatch(bulkEntryActions.toggleYouth({ youth, checked }))
            }
            youth={youth}
            key={youth.memberId}
          />
        ))}
        <ConditionalRender value={eligible.length === 0}>
          <BaseMessage
            title={
              <FormattedMessage id="page.mbcBulkEntry.noYouthEligible.title" />
            }
          />
        </ConditionalRender>
        <ConditionalRender value={notEligible.length > 0}>
          <Divider className={styles.selectYouthDivider} />
          <Text font="body-overline">
            <FormattedMessage id="page.mbcBulkEntry.youths.notEligible" />
          </Text>
          {notEligible.map(youth => (
            <YouthItem
              checked={selectedYouthByMID.has(youth.memberId)}
              onSelect={checked =>
                dispatch(bulkEntryActions.toggleYouth({ youth, checked }))
              }
              youth={youth}
              disabled
              key={youth.memberId}
              errors={errors[youth.memberId]?.map(err => err.error)}
            />
          ))}
        </ConditionalRender>
      </Flex>
    </Card>
  );
}
